import { useEffect, useState } from 'react';

import { Modal, PageHeader, Skeleton } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { getChargePoint, getPhotos } from '../../hooks/chargepoints';
import { getConnectorTypes } from '../../hooks/connectors';
import { getUserPrivateStation } from '../../hooks/users';
import StationConfigForm from '../organisms/StationForm/StationConfigForm';
import { ListSkeleton } from '../molecules/ListSkeleton/ListSkeleton';
import PushNotificationConfigForm from '../organisms/PushNotificationConfigForm';
import { getLicense } from '../../hooks/license';

const StationEditPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;

  h1 {
    font-size: 24px;
  }
`;

const StationConfigFormWrapper = styled.div`
  background-color: #fff;
  padding: 12px 24px 24px;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);
  height: 898px;

  @media (min-width: 1024px) {
    width: 980px;
  }

  @media (min-width: 1366px) {
    width: 1240px;
  }

  .ant-tabs {
    width: 100%;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme.primary};
  }

  .ant-tabs-tab {
    &:hover,
    &:active,
    &:focus {
      color: ${({ theme }) => theme.primary};
    }
  }

  .ant-tabs-ink-bar {
    background: ${({ theme }) => theme.primary};
  }

  .ant-tabs-top > .ant-tabs-nav {
    margin-bottom: 24px;
  }

  form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    label:first-child {
      margin-right: 10px;
    }

    .submit_button {
      margin: 24px 0 0 0;
      background-color: ${({ theme }) => theme.primary};
      border-color: ${({ theme }) => theme.primary};
      display: flex;
      align-self: flex-end;
      border-radius: 3px;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    &:hover,
    &:active,
    &:focus {
      border-color: ${({ theme }) => theme.primary};
    }
  }

  .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: ${({ theme }) => theme.primary};
  }

  .ant-switch-checked {
    background-color: ${({ theme }) => theme.primary};
  }
`;

export const Legend = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  font-weight: 300;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
`;

const StationEditPage = () => {
  const { id } = useParams();
  const [chargePoint, setChargePoint] = useState(null);
  const [connectorTypesList, setConnectorTypesList] = useState({});
  const [usersPrivateStation, setUsersPrivateStation] = useState([]);
  const [stationPhotos, setStationPhotos] = useState({});
  const [stationType, setStationType] = useState('');
  const [
    modalPushNotificationNewStationFast,
    setModalPushNotificationNewStationFast,
  ] = useState({
    visible: false,
    message: '',
    subject: '',
  });
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseStatusLoading, setLicenseStatusLoading] = useState(true);

  const history = useHistory();

  const fetchLicenses = async (chargePointDetail) => {
    try {
      setLicenseStatusLoading(true);

      if (chargePointDetail.licenseCode) {
        const license = await getLicense(chargePointDetail.licenseCode);

        if (license.data[0]) {
          setLicenseStatus(license.data[0].status);
        }

        setLicenseStatusLoading(false);
      }
    } catch {
      //
    } finally {
      setLicenseStatusLoading(false);
    }
  };

  useEffect(() => {
    (async function () {
      const { chargePointDetail } = await getChargePoint(id);
      setChargePoint(chargePointDetail);
      setStationType(chargePointDetail.typeOf);
      const connectorTypesData = await getConnectorTypes();
      setConnectorTypesList(connectorTypesData);
      const { chargePointPhotos } = await getPhotos(
        chargePointDetail?.chargeBoxId,
      );
      setStationPhotos(
        Array.isArray(chargePointPhotos) ? chargePointPhotos : [],
      );
      const { userPrivateStationList } = await getUserPrivateStation(id);
      setUsersPrivateStation(
        userPrivateStationList?.filter((u) => u.owner || u.bind_status === 'ACCEPTED') ?? [],
      );

      fetchLicenses(chargePointDetail);
    })();
  }, [id]);

  const loadUserPrivateStations = async () => {
    try {
      const { userPrivateStationList } = await getUserPrivateStation(id);

      setUsersPrivateStation(
        userPrivateStationList?.filter((u) => u.owner || u.bind_status === 'ACCEPTED') ?? [],
      );
    } catch {
      //
    }
  };

  useEffect(() => {
    if (stationType === 'PRIVATE') {
      loadUserPrivateStations();
    }
  }, [stationType]);

  if (chargePoint === null || !connectorTypesList?.list?.length)
    return (
      <StationEditPageWrapper>
        <PageHeader
          className='site-page-header'
          onBack={() => history.goBack()}
          title={T('Editing Chargebox')}
        />

        <StationConfigFormWrapper>
          <ListSkeleton />
        </StationConfigFormWrapper>
      </StationEditPageWrapper>
    );

  if (chargePoint?.error)
    return (
      <StationEditPageWrapper>
        <p>Nenhuma estação encontrada com este id.</p>
      </StationEditPageWrapper>
    );

  const {
    chargeBoxPk,
    chargeBoxId,
    endpointAddress,
    address: { addressPk, city, houseNumber, state, street, zipCode, country },
    description,
    locationLatitude,
    locationLongitude,
    showLocation,
    typeOf,
    openTime,
    closeTime,
    connectors,
    hasPayment,
    moneyPerKilowattCost,
    moneyPerDurationCost,
    moneyPerKilowattIncome,
    moneyPerDurationIncome,
    moneyPerTransactionIncome,
    hasSocPercentage,
    isOpen_24Hours,
    isHubjectCompatible,
    chargeBoxGroupPk,
    chargeBoxGroupName,
    externalId,
    paymentChargeTypeCost,
    paymentChargeTypeIncome,
    paymentChargeByIncome,
    taxIncome,
    reservation,
    tenantPk,
    deviceReaderMac,
    isOcpiCompatible,
    licenseCode,
  } = chargePoint;

  const initialValues = {
    ...chargePoint,
    chargeBoxPk: chargeBoxPk,
    chargeBoxId: chargeBoxId,
    endpointAddress: endpointAddress,
    address: {
      addressPk: addressPk || null,
      city: city || '',
      country: country,
      empty: false,
      houseNumber: houseNumber || '',
      state: state || '',
      street: street || '',
      zipCode: zipCode || '',
    },
    description: description,
    locationLatitude: locationLatitude,
    locationLongitude: locationLongitude,
    showLocation: showLocation,
    typeOf: typeOf,
    isOpen_24Hours: isOpen_24Hours || chargePoint.scheduleType === '24H' || false,
    hasSocPercentage: hasSocPercentage || false,
    openTime: openTime || null,
    closeTime: closeTime || null,
    customizePerDay: chargePoint.scheduleType === 'CUSTOM',
    autoStopOnClose: chargePoint.autoStopOnClose || false,
    schedules: (chargePoint.schedules || []).map((s) => ({
      dayOfWeek: s.dayOfWeek || s.day_of_week,
      startTime: s.startTime || s.start_time,
      endTime: s.endTime || s.end_time,
    })),
    connectors: connectors.map((connector) => {
      return {
        ...connector,
        connectorTypeId: connectorTypesList?.list?.find(
          ({ name }) => name === connector.connectorType,
        )?.id,
        unusualStatus: connector.unusualStatus
          ? String(connector.unusualStatus).toUpperCase()
          : null,
        idleConnectorFee: connector.idleConnectorFee ?? false,
        idleFeeDefaultAmount: connector.idleFeeDefaultAmount ?? null,
        idleExemptAlwaysCharge: connector.idleExemptAlwaysCharge ?? null,
        idleExemptCustomizePerDay: connector.idleExemptCustomizePerDay ?? null,
        idleExemptSchedules: connector.idleExemptSchedules ?? [],
      };
    }),
    hasPayment: hasPayment || false,
    moneyPerKilowattCost: moneyPerKilowattCost,
    moneyPerDurationCost: moneyPerDurationCost,
    moneyPerKilowattIncome: moneyPerKilowattIncome,
    moneyPerDurationIncome: moneyPerDurationIncome,
    moneyPerTransactionIncome: moneyPerTransactionIncome,
    isHubjectCompatible: isHubjectCompatible || false,
    chargeBoxGroupPk: chargeBoxGroupPk || null,
    chargeBoxGroupName: chargeBoxGroupName || T('Unspecified'),
    externalId: externalId,
    paymentChargeTypeCost: paymentChargeTypeCost,
    paymentChargeTypeIncome: paymentChargeTypeIncome,
    paymentChargeByIncome: paymentChargeByIncome,
    taxIncome: taxIncome * 100,
    reservation: reservation || false,
    photos: Array.isArray(stationPhotos) ? stationPhotos : [],
    deletedPhotos: [],
    tenant_pk: tenantPk,
    isTenantDeviceReader: !!deviceReaderMac,
    isOcpiCompatible: isOcpiCompatible || false,
    deviceReaderMac,
    licenseCode,
    idleExemptAlwaysCharge: chargePoint?.idleExemptAlwaysCharge ?? true,
    idleExemptCustomizePerDay: chargePoint?.idleExemptCustomizePerDay ?? false,
    idleExemptSchedules: chargePoint?.idleExemptSchedules ?? [],
  };

  return (
    <StationEditPageWrapper>
      <PageHeader
        className='site-page-header'
        onBack={() => history.goBack()}
        title={T('Editing Chargebox')}
        subTitle={description}
      />

      {!!modalPushNotificationNewStationFast && (
        <Modal
          visible={modalPushNotificationNewStationFast.visible}
          title={'Enviar push'}
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { display: 'none' } }}
          okText='Finalizar'
          onOk={() => { }}
          onCancel={() => { }}
        >
          <Legend>
            Nova estação de recarga DC rápida detectada! Você pode enviar um
            push para notificar os usuários sobre esta estação. Caso prefira
            apenas continuar sem enviar o push, clique em Finalizar abaixo.
          </Legend>

          <PushNotificationConfigForm
            onSuccess={() => {
              setModalPushNotificationNewStationFast({
                message: '',
                subject: '',
                visible: false,
              });

              setTimeout(() => {
                history.push('/stations');
              }, 1000);
            }}
            onCancel={() => {
              setModalPushNotificationNewStationFast({
                message: '',
                subject: '',
                visible: false,
              });

              setTimeout(() => history.push('/stations'), 1000);
            }}
            initialValues={{
              message: modalPushNotificationNewStationFast.message,
              subject: modalPushNotificationNewStationFast.subject,
            }}
            style={{ width: '100%' }}
          />
        </Modal>
      )}
      <StationConfigForm
        initialValues={initialValues}
        type='update'
        chargeBoxPk={id}
        setModalPushNotificationNewStationFast={
          setModalPushNotificationNewStationFast
        }
        setStationType={setStationType}
        usersPrivateStation={usersPrivateStation}
        stationPhotos={stationPhotos}
        licenseStatus={licenseStatus}
        setLicenseStatus={setLicenseStatus}
        licenseStatusLoading={licenseStatusLoading}
      />
    </StationEditPageWrapper>
  );
};

export default StationEditPage;
